import { KeyRound } from 'lucide-react';
import { useRef } from 'react';

import { CopyButton } from '@/components/common/CopyButton';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

import { webhookPath } from './types';

export interface KeyModalState {
  variant: 'create' | 'rotate';
  environmentName: string;
  environmentId: string;
  apiKey: string;
}

const WARNING_BODY = 'This key is shown only once.';

/**
 * API-key display-once modal (design §8.3) — one modal, two variants.
 *
 * P6 display-once mechanics: the plaintext arrives from the create/rotate
 * mutation response into the parent's React state (`state.apiKey`); closing the
 * modal drops it, and it is never written to localStorage, the RTK Query cache
 * (the parent also `reset()`s the mutation), or logs. Reopening is impossible —
 * the list shows only the prefix.
 *
 * Backdrop clicks do NOT close this dialog (`onPointerDownOutside` prevented —
 * accidental dismissal is the one irreversible UI mistake here); Esc still
 * closes (keyboard parity; recovery is rotation).
 */
export function ApiKeyOnceModal({
  state,
  onAcknowledge,
}: {
  state: KeyModalState | null;
  onAcknowledge: () => void;
}) {
  const acknowledgeRef = useRef<HTMLButtonElement>(null);

  if (!state) {
    return null;
  }

  const { variant, environmentName, environmentId, apiKey } = state;
  const endpoint = webhookPath(environmentId);

  return (
    <Dialog open onOpenChange={(next) => !next && onAcknowledge()}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(event) => event.preventDefault()}
        // §8.3 keyboard: focus lands on "I've saved it".
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          acknowledgeRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            {variant === 'create' ? 'Save your environment key' : 'Save your new key'}
          </DialogTitle>
          <DialogDescription>
            {variant === 'create' ? (
              <>
                {WARNING_BODY} It can send reports to “{environmentName}” — nothing else.
              </>
            ) : (
              <>
                {WARNING_BODY} The old key stopped working when you rotated it. This new key can
                send reports to “{environmentName}” — nothing else.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="api-key-once-value">API key</Label>
            <div className="flex items-start gap-2">
              <output
                id="api-key-once-value"
                className="min-w-0 flex-1 break-all rounded-md bg-muted p-3 font-mono text-sm"
              >
                {apiKey}
              </output>
              <CopyButton value={apiKey} label="Copy API key" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="api-key-once-webhook">Webhook endpoint</Label>
            <div className="flex items-start gap-2">
              <output
                id="api-key-once-webhook"
                className="min-w-0 flex-1 break-all rounded-md bg-muted p-3 font-mono text-sm"
              >
                {endpoint}
              </output>
              <CopyButton value={endpoint} label="Copy webhook endpoint" />
            </div>
            <p className="text-sm text-muted-foreground">
              Send reports here with the key in the x-api-key header.
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            If you lose the key, rotate it from the environment&apos;s menu.
          </p>
        </div>

        <DialogFooter>
          <Button ref={acknowledgeRef} onClick={onAcknowledge}>
            I&apos;ve saved it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
